import React from 'react';
import { Save, Edit3, Plus, Calculator, Trash2 } from 'lucide-react';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Cell,
  Legend,
} from 'recharts';

import EditableInput from './common/EditableInput';
import { CHART_COLORS as COLORS } from './common/Constants';

/**
 * DebtTab – Enhanced with interest tracking and payoff calculations
 *
 * Props
 *  ─ debts:                Debt[] (now includes apr, monthlyPayment, totalInterestPaid)
 *  ─ editMode:             boolean
 *  ─ onToggleEdit():       flip edit state
 *  ─ updateDebt(i,k,val):  parent updater
 *  ─ addDebt():            append a blank debt record
 *  ─ removeDebt(i):        delete debt record
 *  ─ chartsReady:          boolean
 */
const DebtTab = ({
  debts,
  editMode,
  onToggleEdit,
  updateDebt,
  addDebt,
  removeDebt,
  chartsReady = true,
}) => {
  // State for extra payment scenario
  const [showComparison, setShowComparison] = React.useState(false);
  // Store extra payment per debt: { debtIndex: { amount: 0, mode: 'dollar' } }
  const [debtExtraPayments, setDebtExtraPayments] = React.useState({});

  // Helper to get extra payment info for a debt
  const getExtraPaymentForDebt = (debtIndex) => {
    return debtExtraPayments[debtIndex] || { amount: 0, mode: 'dollar' };
  };

  // Helper to update extra payment for a debt
  const updateDebtExtraPayment = (debtIndex, field, value) => {
    setDebtExtraPayments(prev => ({
      ...prev,
      [debtIndex]: {
        ...(prev[debtIndex] || { amount: 0, mode: 'dollar' }),
        [field]: value
      }
    }));
  };

  // Calculate monthly interest and payoff projections for each debt
  const calculateDebtMetrics = (debt) => {
    const monthlyRate = (debt.apr || 0) / 100 / 12;
    const monthlyInterest = debt.current * monthlyRate;
    const minimumPayment = debt.monthlyPayment || 0;
    
    let monthsToPayoff = 0;
    let totalInterestRemaining = 0;
    
    if (minimumPayment > monthlyInterest && debt.current > 0) {
      // Calculate payoff time using amortization formula
      const principal = minimumPayment - monthlyInterest;
      if (principal > 0) {
        monthsToPayoff = Math.ceil(Math.log(1 + (debt.current * monthlyRate) / principal) / Math.log(1 + monthlyRate));
        totalInterestRemaining = (minimumPayment * monthsToPayoff) - debt.current;
      }
    }
    
    return {
      monthlyInterest: monthlyInterest,
      monthsToPayoff: monthsToPayoff,
      totalInterestRemaining: Math.max(0, totalInterestRemaining),
      principalPayment: Math.max(0, minimumPayment - monthlyInterest)
    };
  };

  // Calculate total debt projection over time
  const calculateDebtProjection = (useExtraPayments = false) => {
    const activeDebts = debts.filter(d => d.current > 0 && d.original > 0);
    if (activeDebts.length === 0) return [];

    // Calculate max months needed
    const maxMonthsBase = Math.max(
      ...activeDebts.map(debt => {
        const metrics = calculateDebtMetrics(debt);
        return metrics.monthsToPayoff || 0;
      }),
      12
    );

    const projectionData = [];
    
    for (let month = 0; month <= Math.min(maxMonthsBase + 60, 120); month++) {
      let totalRemaining = 0;
      let totalInterestPaid = 0;
      
      activeDebts.forEach((debt, debtIdx) => {
        const monthlyRate = (debt.apr || 0) / 100 / 12;
        const basePayment = debt.monthlyPayment || 0;
        
        // Calculate extra payment for this specific debt
        let extraPayment = 0;
        if (useExtraPayments) {
          const extraInfo = getExtraPaymentForDebt(debtIdx);
          if (extraInfo.mode === 'dollar') {
            extraPayment = extraInfo.amount || 0;
          } else {
            // Percentage mode - percentage of current monthly payment
            extraPayment = basePayment * ((extraInfo.amount || 0) / 100);
          }
        }
        
        const totalPayment = basePayment + extraPayment;
        
        let balance = debt.current;
        let interestAccrued = 0;
        
        for (let m = 0; m < month; m++) {
          if (balance <= 0) break;
          
          const interest = balance * monthlyRate;
          interestAccrued += interest;
          const principal = Math.min(totalPayment - interest, balance);
          
          if (principal <= 0) {
            balance += interest;
          } else {
            balance = balance + interest - totalPayment;
            balance = Math.max(0, balance);
          }
        }
        
        totalRemaining += Math.max(0, balance);
        totalInterestPaid += interestAccrued;
      });

      projectionData.push({
        month: month,
        totalDebt: Math.round(totalRemaining),
        totalInterest: Math.round(totalInterestPaid),
        monthLabel: month === 0 ? 'Now' : `${month}mo`
      });
      
      // Stop if all debt is paid off
      if (totalRemaining === 0 && month > 0) break;
    }

    return projectionData;
  };

  // Calculate both projections
  const currentProjection = calculateDebtProjection(false);
  const acceleratedProjection = showComparison ? calculateDebtProjection(true) : [];
  
  // Merge projections for chart display
  const maxLength = Math.max(currentProjection.length, acceleratedProjection.length);
  const projectionData = Array.from({ length: maxLength }, (_, idx) => {
    const current = currentProjection[idx];
    const accelerated = acceleratedProjection[idx];
    return {
      month: idx,
      monthLabel: idx === 0 ? 'Now' : `${idx}mo`,
      totalDebt: current ? current.totalDebt : 0,
      totalInterest: current ? current.totalInterest : 0,
      acceleratedDebt: accelerated ? accelerated.totalDebt : null,
      acceleratedInterest: accelerated ? accelerated.totalInterest : null
    };
  });

  // Calculate savings metrics
  const currentPayoffMonth = currentProjection.findIndex(d => d.totalDebt === 0);
  const acceleratedPayoffMonth = showComparison ? acceleratedProjection.findIndex(d => d.totalDebt === 0) : -1;
  const monthsSaved = currentPayoffMonth > 0 && acceleratedPayoffMonth > 0 
    ? currentPayoffMonth - acceleratedPayoffMonth 
    : 0;
  
  const currentTotalInterest = currentPayoffMonth > 0 
    ? currentProjection[currentPayoffMonth]?.totalInterest || 0 
    : 0;
  const acceleratedTotalInterest = acceleratedPayoffMonth > 0 
    ? acceleratedProjection[acceleratedPayoffMonth]?.totalInterest || 0 
    : 0;
  const interestSaved = currentTotalInterest - acceleratedTotalInterest;
  
  // Calculate total extra payment per month
  const totalExtraPayment = debts.reduce((sum, debt, idx) => {
    const extraInfo = getExtraPaymentForDebt(idx);
    const basePayment = debt.monthlyPayment || 0;
    let extra = 0;
    if (extraInfo.mode === 'dollar') {
      extra = extraInfo.amount || 0;
    } else {
      extra = basePayment * ((extraInfo.amount || 0) / 100);
    }
    return sum + extra;
  }, 0);

  // Prepare chart data - only include debts with original amounts > 0
  const chartData = debts
    .filter(debt => debt.original > 0)
    .map(debt => {
      const metrics = calculateDebtMetrics(debt);
      return {
        name: debt.name.length > 15 ? debt.name.substring(0, 15) + '...' : debt.name,
        fullName: debt.name,
        paidOff: debt.paidOff,
        remaining: debt.current,
        original: debt.original,
        monthlyInterest: metrics.monthlyInterest,
        monthsToPayoff: metrics.monthsToPayoff
      };
    });

  // Calculate totals for summary
  const summaryData = debts
    .filter(d => d.original > 0)
    .reduce((acc, debt) => {
      const metrics = calculateDebtMetrics(debt);
      return {
        totalOriginal: acc.totalOriginal + debt.original,
        totalRemaining: acc.totalRemaining + debt.current,
        totalPaidOff: acc.totalPaidOff + (debt.original - debt.current),
        totalMonthlyInterest: acc.totalMonthlyInterest + metrics.monthlyInterest,
        totalMonthlyPayments: acc.totalMonthlyPayments + (debt.monthlyPayment || 0),
        totalInterestPaid: acc.totalInterestPaid + (debt.totalInterestPaid || 0),
        totalInterestRemaining: acc.totalInterestRemaining + metrics.totalInterestRemaining
      };
    }, {
      totalOriginal: 0,
      totalRemaining: 0,
      totalPaidOff: 0,
      totalMonthlyInterest: 0,
      totalMonthlyPayments: 0,
      totalInterestPaid: 0,
      totalInterestRemaining: 0
    });

  return (
    <div className="space-y-6">
      {/* Line Chart - Total Debt Projection */}
      {projectionData.length > 0 && (
        <div className="bg-arc-elevated p-6 rounded-lg shadow-md">
          <div className="flex flex-wrap justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">Total Debt Projection</h3>
            
            {/* Comparison Toggle */}
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={showComparison}
                  onChange={(e) => setShowComparison(e.target.checked)}
                  className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                />
                <span className="text-sm font-medium">Compare with Extra Payment</span>
              </label>
            </div>
          </div>

          {/* Extra Payment Input */}
          {showComparison && (
            <div className="mb-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <h4 className="text-sm font-semibold text-blue-900 mb-3">Configure Extra Payments per Debt</h4>
              
              {/* Per-debt extra payment inputs */}
              <div className="space-y-3 mb-4">
                {debts.filter(d => d.current > 0 && d.original > 0).map((debt, idx) => {
                  const extraInfo = getExtraPaymentForDebt(idx);
                  const basePayment = debt.monthlyPayment || 0;
                  const extraAmount = extraInfo.mode === 'dollar' 
                    ? (extraInfo.amount || 0)
                    : basePayment * ((extraInfo.amount || 0) / 100);
                  const newPayment = basePayment + extraAmount;
                  
                  return (
                    <div key={idx} className="bg-arc-elevated p-3 rounded-lg border border-blue-200">
                      <div className="flex flex-wrap items-center gap-3">
                        {/* Debt Name */}
                        <div className="flex-1 min-w-[150px]">
                          <p className="font-medium text-sm text-arc-ink">{debt.name}</p>
                          <p className="text-xs text-arc-dim">
                            Current: ${basePayment.toLocaleString()}/mo
                          </p>
                        </div>
                        
                        {/* Mode Toggle */}
                        <div className="flex gap-1 bg-white/[0.05] p-1 rounded">
                          <button
                            onClick={() => updateDebtExtraPayment(idx, 'mode', 'dollar')}
                            className={`px-3 py-1 text-xs rounded ${
                              extraInfo.mode === 'dollar' 
                                ? 'bg-blue-600 text-white' 
                                : 'bg-transparent text-arc-muted'
                            }`}
                          >
                            $
                          </button>
                          <button
                            onClick={() => updateDebtExtraPayment(idx, 'mode', 'percentage')}
                            className={`px-3 py-1 text-xs rounded ${
                              extraInfo.mode === 'percentage' 
                                ? 'bg-blue-600 text-white' 
                                : 'bg-transparent text-arc-muted'
                            }`}
                          >
                            %
                          </button>
                        </div>
                        
                        {/* Amount Input */}
                        <div className="flex items-center gap-2 min-w-[120px]">
                          <input
                            type="number"
                            min="0"
                            step={extraInfo.mode === 'dollar' ? '10' : '1'}
                            value={extraInfo.amount || 0}
                            onChange={(e) => updateDebtExtraPayment(idx, 'amount', Number(e.target.value))}
                            className="w-full px-2 py-1 text-sm border border-blue-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="0"
                          />
                          <span className="text-xs text-arc-muted">
                            {extraInfo.mode === 'dollar' ? '$' : '%'}
                          </span>
                        </div>
                        
                        {/* New Payment Display */}
                        <div className="text-right min-w-[100px]">
                          <p className="text-xs text-arc-muted">New Payment</p>
                          <p className="font-semibold text-sm text-green-600">
                            ${newPayment.toFixed(2)}/mo
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Total Summary */}
              <div className="flex flex-wrap gap-4 pt-3 border-t border-blue-300">
                <div className="flex-1 bg-arc-elevated rounded-lg p-3 border border-blue-300">
                  <h5 className="text-sm font-semibold text-blue-900 mb-2">Payment Summary</h5>
                  <div className="grid grid-cols-2 gap-3 text-xs">
                    <div>
                      <p className="text-arc-muted">Total Extra/Month</p>
                      <p className="font-bold text-blue-600 text-base">
                        ${totalExtraPayment.toFixed(2)}
                      </p>
                    </div>
                    <div>
                      <p className="text-arc-muted">New Total Payment</p>
                      <p className="font-bold text-blue-600 text-base">
                        ${(summaryData.totalMonthlyPayments + totalExtraPayment).toFixed(2)}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Savings Summary */}
                {monthsSaved > 0 && (
                  <div className="flex-1 bg-arc-elevated rounded-lg p-3 border border-blue-300">
                    <h5 className="text-sm font-semibold text-green-900 mb-2">Projected Savings</h5>
                    <div className="grid grid-cols-2 gap-3 text-xs">
                      <div>
                        <p className="text-arc-muted">Time Saved</p>
                        <p className="font-bold text-green-600 text-base">
                          {monthsSaved} months
                        </p>
                      </div>
                      <div>
                        <p className="text-arc-muted">Interest Saved</p>
                        <p className="font-bold text-green-600 text-base">
                          ${interestSaved.toLocaleString()}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {chartsReady ? (
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={projectionData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="monthLabel"
                  fontSize={12}
                  interval={Math.ceil(projectionData.length / 12)}
                />
                <YAxis 
                  tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                  fontSize={12}
                />
                <Tooltip 
                  formatter={(value, name) => {
                    if (name === 'Current Plan') return [`$${value.toLocaleString()}`, 'Current Plan'];
                    if (name === 'With Extra Payment') return [`$${value.toLocaleString()}`, 'With Extra Payment'];
                    return [value, name];
                  }}
                  labelFormatter={(label) => `Month: ${label}`}
                />
                <Legend />
                <Line 
                  type="monotone" 
                  dataKey="totalDebt" 
                  stroke="#3b82f6" 
                  strokeWidth={3}
                  name="Current Plan"
                  dot={false}
                />
                {showComparison && (
                  <Line 
                    type="monotone" 
                    dataKey="acceleratedDebt" 
                    stroke="#10b981" 
                    strokeWidth={3}
                    strokeDasharray="5 5"
                    name="With Extra Payment"
                    dot={false}
                  />
                )}
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[300px] flex items-center justify-center bg-white/[0.03] rounded">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                <p className="text-arc-muted">Loading chart...</p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Bar Chart Section */}
      {chartData.length > 0 && (
        <div className="bg-arc-elevated p-6 rounded-lg shadow-md">
          <h3 className="text-lg font-semibold mb-4">Debt Payoff Progress</h3>
          {chartsReady ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="name" 
                  angle={-45}
                  textAnchor="end"
                  height={80}
                  fontSize={12}
                />
                <YAxis 
                  tickFormatter={(value) => `${value.toFixed(0)}%`}
                  domain={[0, 100]}
                />
                <Tooltip 
                  formatter={(value, name) => [`${value.toFixed(1)}%`, 'Paid Off']}
                  labelFormatter={(label) => {
                    const debt = chartData.find(d => d.name === label);
                    return debt ? debt.fullName : label;
                  }}
                />
                <Bar dataKey="paidOff" name="Paid Off Percentage">
                  {chartData.map((entry, index) => (
                    <Cell 
                      key={`cell-${index}`} 
                      fill={entry.paidOff >= 100 ? '#10b981' : COLORS[index % COLORS.length]} 
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[300px] flex items-center justify-center bg-white/[0.03] rounded">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                <p className="text-arc-muted">Loading chart...</p>
              </div>
            </div>
          )}
        </div>
      )}

      {debts.filter(d => d.original > 0).length > 0 && (
          <div className="mt-6 p-4 bg-white/[0.03] rounded-lg">
            <h4 className="font-semibold mb-3">Debt Summary</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
              {/* Original totals */}
              <div>
                <p className="text-arc-muted">Total Original Debt</p>
                <p className="font-semibold text-lg">
                  ${summaryData.totalOriginal.toLocaleString()}
                </p>
              </div>
              <div>
                <p className="text-arc-muted">Total Remaining</p>
                <p className="font-semibold text-lg text-red-600">
                  ${summaryData.totalRemaining.toLocaleString()}
                </p>
              </div>
              <div>
                <p className="text-arc-muted">Total Paid Off</p>
                <p className="font-semibold text-lg text-green-600">
                  ${summaryData.totalPaidOff.toLocaleString()}
                </p>
              </div>
              <div>
                <p className="text-arc-muted">Overall Progress</p>
                <p className="font-semibold text-lg text-blue-600">
                  {summaryData.totalOriginal > 0 ? 
                    ((summaryData.totalPaidOff / summaryData.totalOriginal) * 100).toFixed(1) : 0}%
                </p>
              </div>
            </div>
            
            {/* Interest summary */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-sm mt-4 pt-4 border-t border-arc-line">
              <div>
                <p className="text-arc-muted">Monthly Interest Cost</p>
                <p className="font-semibold text-lg text-yellow-600">
                  ${summaryData.totalMonthlyInterest.toFixed(2)}
                </p>
              </div>
              <div>
                <p className="text-arc-muted">Monthly Payments</p>
                <p className="font-semibold text-lg text-blue-600">
                  ${summaryData.totalMonthlyPayments.toLocaleString()}
                </p>
              </div>
              <div>
                <p className="text-arc-muted">Total Interest Paid</p>
                <p className="font-semibold text-lg text-orange-600">
                  ${summaryData.totalInterestPaid.toLocaleString()}
                </p>
              </div>
              <div>
                <p className="text-arc-muted">Est. Remaining Interest</p>
                <p className="font-semibold text-lg text-red-600">
                  ${summaryData.totalInterestRemaining.toFixed(2)}
                </p>
              </div>
            </div>
          </div>
        )}

      {/* Debt Management Section */}
      <div className="bg-arc-elevated p-6 rounded-lg shadow-md">
        {/* Header */}
        <div className="flex flex-wrap justify-between items-center gap-2 mb-4">
          <h3 className="text-lg font-semibold">Debt Tracking</h3>

          <div className="flex gap-2">
            <button
              onClick={addDebt}
              className="flex items-center gap-2 px-3 py-2 rounded-lg bg-purple-600 text-white hover:opacity-90"
            >
              <Plus size={16} /> Add&nbsp;Debt
            </button>

            <button
              onClick={onToggleEdit}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg ${
                editMode ? 'bg-green-600 text-white' : 'bg-blue-600 text-white'
              } hover:opacity-90`}
            >
              {editMode ? <Save size={16} /> : <Edit3 size={16} />}{' '}
              {editMode ? 'Save' : 'Edit'}
            </button>
          </div>
        </div>

        {/* Debt Cards */}
        <div className="space-y-4">
          {debts
            .filter((d) => d.original > 0 || editMode)
            .map((debt, idx) => {
              const metrics = calculateDebtMetrics(debt);
              return (
                <div key={idx} className="border rounded-lg p-4">
                  {/* Title */}
                  <div className="flex justify-between items-center mb-2">
                    <h4 className="font-medium text-lg">
                      {editMode ? (
                        <EditableInput
                          type="text"
                          value={debt.name}
                          onChange={(v) => updateDebt(idx, 'name', v)}
                          className="font-medium text-lg"
                        />
                      ) : (
                        debt.name
                      )}
                    </h4>
                    {editMode && (
                      <button
                        onClick={() => removeDebt(idx)}
                        className="text-red-600 hover:text-red-800 p-2"
                        title="Delete debt"
                      >
                        <Trash2 size={18} />
                      </button>
                    )}
                  </div>

                  {/* Progress bar */}
                  <div className="w-full bg-white/[0.08] rounded-full h-3 mb-3">
                    <div
                      className={`h-3 rounded-full transition-all duration-300 ${
                        debt.paidOff >= 100 ? 'bg-green-500' : 'bg-blue-500'
                      }`}
                      style={{ width: `${Math.min(debt.paidOff, 100)}%` }}
                    />
                  </div>

                  {/* Main stats grid */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mb-4">
                    {/* Original Amount */}
                    <div>
                      <p className="text-arc-muted mb-1">Original Amount</p>
                      <p className="font-semibold">
                        {editMode ? (
                          <EditableInput
                            value={debt.original}
                            onChange={(v) => updateDebt(idx, 'original', v)}
                            className="w-full text-sm"
                          />
                        ) : (
                          `$${debt.original.toLocaleString()}`
                        )}
                      </p>
                    </div>

                    {/* Remaining Balance */}
                    <div>
                      <p className="text-arc-muted mb-1">Remaining Balance</p>
                      <p className="font-semibold">
                        {editMode ? (
                          <EditableInput
                            value={debt.current}
                            onChange={(v) => updateDebt(idx, 'current', v)}
                            className="w-full text-sm"
                          />
                        ) : (
                          `$${debt.current.toLocaleString()}`
                        )}
                      </p>
                    </div>

                    {/* APR */}
                    <div>
                      <p className="text-arc-muted mb-1">APR (%)</p>
                      <p className="font-semibold">
                        {editMode ? (
                          <EditableInput
                            value={debt.apr || 0}
                            onChange={(v) => updateDebt(idx, 'apr', v)}
                            className="w-full text-sm"
                          />
                        ) : (
                          `${(debt.apr || 0).toFixed(2)}%`
                        )}
                      </p>
                    </div>

                    {/* Monthly Payment */}
                    <div>
                      <p className="text-arc-muted mb-1">Monthly Payment</p>
                      <p className="font-semibold">
                        {editMode ? (
                          <EditableInput
                            value={debt.monthlyPayment || 0}
                            onChange={(v) => updateDebt(idx, 'monthlyPayment', v)}
                            className="w-full text-sm"
                          />
                        ) : (
                          `$${(debt.monthlyPayment || 0).toLocaleString()}`
                        )}
                      </p>
                    </div>
                  </div>

                  {/* Interest calculations */}
                  {debt.current > 0 && debt.apr > 0 && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-3">
                      <div className="flex items-center gap-2 mb-2">
                        <Calculator size={16} className="text-yellow-600" />
                        <h5 className="font-medium text-yellow-800">Interest Analysis</h5>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
                        <div>
                          <p className="text-yellow-700">Monthly Interest</p>
                          <p className="font-semibold text-yellow-900">
                            ${metrics.monthlyInterest.toFixed(2)}
                          </p>
                        </div>
                        <div>
                          <p className="text-yellow-700">Principal Payment</p>
                          <p className="font-semibold text-yellow-900">
                            ${metrics.principalPayment.toFixed(2)}
                          </p>
                        </div>
                        <div>
                          <p className="text-yellow-700">Months to Payoff</p>
                          <p className="font-semibold text-yellow-900">
                            {metrics.monthsToPayoff > 0 ? `${metrics.monthsToPayoff} months` : 'N/A'}
                          </p>
                        </div>
                        <div>
                          <p className="text-yellow-700">Remaining Interest</p>
                          <p className="font-semibold text-yellow-900">
                            ${metrics.totalInterestRemaining.toFixed(2)}
                          </p>
                        </div>
                      </div>
                      {debt.monthlyPayment <= metrics.monthlyInterest && debt.current > 0 && (
                        <div className="mt-2 p-2 bg-red-100 border border-red-300 rounded text-xs">
                          <p className="text-red-800 font-medium">
                            ⚠️ Warning: Monthly payment is less than interest. Debt will grow!
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Progress display */}
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-arc-muted mb-1">Progress</p>
                      <p className={`font-semibold ${debt.paidOff >= 100 ? 'text-green-600' : 'text-blue-600'}`}>
                        {debt.paidOff.toFixed(1)}% paid off
                      </p>
                    </div>
                    <div>
                      <p className="text-arc-muted mb-1">Total Interest Paid</p>
                      <p className="font-semibold">
                        {editMode ? (
                          <EditableInput
                            value={debt.totalInterestPaid || 0}
                            onChange={(v) => updateDebt(idx, 'totalInterestPaid', v)}
                            className="w-full text-sm"
                          />
                        ) : (
                          `$${(debt.totalInterestPaid || 0).toLocaleString()}`
                        )}
                      </p>
                    </div>
                  </div>

                  {/* Fully paid indicator */}
                  {debt.paidOff >= 100 && (
                    <div className="mt-3 p-2 bg-green-100 border border-green-300 rounded-lg">
                      <p className="text-green-800 text-sm font-medium text-center">
                        🎉 Congratulations! This debt is fully paid off!
                      </p>
                    </div>
                  )}
                </div>
              );
            })}
        </div>
        
      </div>
    </div>
  );
};

export default DebtTab;